/* Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Else, If, Then } from "react-if";
import { useUpdateEffect } from "react-use";
import { Tab, Tabs } from "@nordcloud/gnui";
import { ReservationsProvider } from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { ROUTES } from "~/routing/routes";
import { isNotEmpty } from "~/tools";
import { ReservationBanner, ReservationPageLoader } from "../components";
import { CoverageChartWrapper } from "../CoverageChartWrapper";
import { getTooltipDetailsCallback } from "../getTooltipDetailsCallback";
import {
  useCheckActiveAccount,
  useGetCurrentTab,
  useGetFilters,
  useGetServiceUsageType,
  useGetTabsData,
  useGetUsageAnalysisData,
  useReservationFilters,
  useReservationState,
} from "../hooks";
import { RecommendationsSettingsWrapper } from "../RecommendationsSettingsWrapper";
import { ReservationsCoverageTable } from "../ReservationsCoverageTable";
import { TabSectionLabel } from "../TabSectionLabel";
import { CoverageChartFilters } from "../types";
import { getPaymentOptionsFilterprovider } from "../utils";
import { AwsUsageAnalysisGraph } from "./AwsUsageAnalysisGraph";

export function AwsCurrentBillingPeriodPage() {
  const {
    activeTab,
    offeringType,
    timePeriod,
    paymentOption,
    termOption,
    isSidebarOpen,
    coverageDetails,
    handleOpenSidebar,
    handleToggleSidebar,
    handleUpdateTermOption,
    handleUpdatePaymentOption,
    handleOfferingType,
    handleSetTimePeriod,
    setActiveTab,
  } = useReservationState(ReservationsProvider.Aws);

  const filters: CoverageChartFilters = useGetFilters();

  const { serviceUsageType, reservationOfferingType } =
    useGetServiceUsageType(activeTab);
  const { isComputeTab, isMachineLearningTab } =
    useGetCurrentTab(serviceUsageType);
  const { currentOfferingType, selectedFilters } = useGetTabsData({
    isComputeTab,
    showFilters: !isMachineLearningTab,
    offeringType,
    reservationOfferingType,
    filters,
  });

  const { tabsSelection, loading } = useCheckActiveAccount(
    ReservationsProvider.Aws
  );

  const {
    loading: isGraphLoading,
    utilisationChartError,
    timeSeries,
    highestCost,
    averageCoverage,
    reservedInstaceCoverage,
    savingsPlanCoverage,
    totalOnDemandCost,
    savedCost,
  } = useGetUsageAnalysisData({
    serviceUsageType,
    timePeriod,
    filters,
    provider: ReservationsProvider.Aws,
  });

  const tooltipDetailCallback = getTooltipDetailsCallback(
    ReservationsProvider.Aws
  );

  const paymentOptionsFilter = getPaymentOptionsFilterprovider(
    ReservationsProvider.Aws
  );

  const { activeFilters, removeFields, clearFilters, removeFilter } =
    useReservationFilters(filters);

  useUpdateEffect(clearFilters, [activeTab]);

  return (
    <LoaderWrap loading={loading} Component={ReservationPageLoader}>
      <Tabs step={activeTab} handleTab={setActiveTab}>
        {tabsSelection.map(
          ({ label, icon, serviceType, isActiveTab, bannerText, idText }) => (
            <Tab
              key={label}
              width="auto"
              label={<TabSectionLabel icon={icon} label={label} />}
            >
              <If condition={isActiveTab}>
                <Then>
                  <CoverageChartWrapper
                    showFiltersOption
                    anyTab={!isMachineLearningTab}
                    idText={idText}
                    serviceType={serviceType}
                    filters={filters}
                    timePeriod={timePeriod}
                    activeFilters={activeFilters}
                    provider={ReservationsProvider.Aws}
                    onSetTimePeriodClick={handleSetTimePeriod}
                    onRemoveFields={removeFields}
                    onClearFilters={clearFilters}
                    onRemoveFilter={removeFilter}
                  >
                    <AwsUsageAnalysisGraph
                      serviceUsageType={serviceUsageType}
                      filters={filters}
                      graphLoading={isGraphLoading}
                      timeSeries={timeSeries}
                      highestCost={highestCost}
                      utilisationChartError={utilisationChartError}
                      averageCoverage={averageCoverage}
                      reservedInstaceCoverage={reservedInstaceCoverage}
                      savingsPlanCoverage={savingsPlanCoverage}
                      savedCost={savedCost}
                      totalOnDemandCost={totalOnDemandCost}
                      tooltipDetailCallback={tooltipDetailCallback}
                    />
                  </CoverageChartWrapper>
                  <RecommendationsSettingsWrapper
                    idText={idText}
                    paymentOption={paymentOption}
                    termOption={termOption}
                    serviceType={serviceType}
                    timePeriod={timePeriod}
                    currentOfferingType={currentOfferingType}
                    selectedFilters={selectedFilters}
                    paymentOptionsFilter={paymentOptionsFilter}
                    provider={ReservationsProvider.Aws}
                    hasFilters={isNotEmpty(activeFilters)}
                    onOfferingType={handleOfferingType}
                    onUpdatePaymentOption={handleUpdatePaymentOption}
                    onUpdateTermOption={handleUpdateTermOption}
                    onClearFilters={clearFilters}
                  />
                  <ReservationsCoverageTable
                    hasGroupByParameter
                    idText={idText}
                    termOption={termOption}
                    paymentOption={paymentOption}
                    coverageDetails={coverageDetails}
                    serviceType={serviceType}
                    selectedFilters={selectedFilters}
                    timePeriod={timePeriod}
                    currentOfferingType={currentOfferingType}
                    isSidebarOpen={isSidebarOpen}
                    provider={ReservationsProvider.Aws}
                    paymentOptionsFilter={paymentOptionsFilter}
                    onOpenSidebar={handleOpenSidebar}
                    onToggleSidebar={handleToggleSidebar}
                  />
                </Then>
                <Else>
                  <ReservationBanner
                    isLink
                    heading="No Data Available"
                    subText={bannerText}
                    buttonText="Add Billing Data"
                    routePath={ROUTES.cloudAccounts.index}
                  />
                </Else>
              </If>
            </Tab>
          )
        )}
      </Tabs>
    </LoaderWrap>
  );
}
